import { axiosClient } from "@/shared/api/axios-client";
import type { ProveedorAdmin } from "@/lib/types";

export interface CrearProveedorInput {
  numeroProveedor: number;
  rfc: string;
  razonSocial: string;
  /** Proveedor extranjero · relaja la validación del RFC (tax ID libre). */
  esExtranjero: boolean;
  /** Banco, Cuenta y CLABE son opcionales · pueden capturarse después al editar. */
  banco: string | null;
  cuenta: string | null;
  clabe: string | null;
}

export interface EditarProveedorInput {
  razonSocial: string;
  /** Banco, Cuenta y CLABE son opcionales · pueden quedar vacíos. */
  banco: string | null;
  cuenta: string | null;
  clabe: string | null;
}

export interface ListarProveedoresParams {
  busqueda?: string;
  soloActivos?: boolean;
  page?: number;
  pageSize?: number;
}

/** Envelope paginado del backend + conteos del catálogo completo para el header. */
export interface PaginaProveedores {
  items: ProveedorAdmin[];
  total: number;
  page: number;
  pageSize: number;
  totalCatalogo: number;
  totalActivos: number;
  /** Sugerencia (max + 1) para el form de alta. */
  siguienteNumero: number;
}

export async function listarProveedoresAdmin(
  params: ListarProveedoresParams = {}
): Promise<PaginaProveedores> {
  const { data } = await axiosClient.get<PaginaProveedores>(
    "/api/admin/proveedores",
    { params }
  );
  return data;
}

export async function crearProveedor(
  input: CrearProveedorInput
): Promise<ProveedorAdmin> {
  const { data } = await axiosClient.post<ProveedorAdmin>(
    "/api/admin/proveedores",
    input
  );
  return data;
}

export async function editarProveedor(
  numero: number,
  input: EditarProveedorInput
): Promise<ProveedorAdmin> {
  const { data } = await axiosClient.patch<ProveedorAdmin>(
    `/api/admin/proveedores/${numero}`,
    input
  );
  return data;
}

export async function darDeBajaProveedor(numero: number): Promise<ProveedorAdmin> {
  const { data } = await axiosClient.post<ProveedorAdmin>(
    `/api/admin/proveedores/${numero}/baja`
  );
  return data;
}

export async function reactivarProveedor(numero: number): Promise<ProveedorAdmin> {
  const { data } = await axiosClient.post<ProveedorAdmin>(
    `/api/admin/proveedores/${numero}/reactivar`
  );
  return data;
}

// --- Importación masiva desde Excel ------------------------------------------

/** Una fila de la importación · campos como texto (el backend parsea el número). */
export interface FilaImportacionProveedor {
  numeroProveedor: string;
  rfc: string;
  razonSocial: string;
  banco: string;
  cuenta: string;
  clabe: string;
  /** Origen · "E" = extranjero, "N"/vacío = nacional. Solo aplica al crear. */
  tipo: string;
}

/** Detalle por fila del preview · qué se hará con cada renglón. */
export interface PreviewFilaProveedor {
  renglon: number;
  numeroProveedor: string;
  rfc: string;
  razonSocial: string;
  estado: "Crear" | "Actualizar" | "Omitir";
  motivo: string | null;
  /** Tipo efectivo · "Nacional" | "Extranjero". */
  tipo: string;
}

/** Resumen que devuelve el backend tras importar (real y dry-run). */
export interface ResultadoImportacionProveedores {
  creados: number;
  actualizados: number;
  omitidos: number;
  errores: string[];
  detalle: PreviewFilaProveedor[];
}

/**
 * Importa (o valida en dry-run) los proveedores. Upsert por número de proveedor.
 * Con `soloValidar=true` el backend corre la misma lógica SIN persistir y
 * devuelve el detalle por fila (para el preview).
 */
export async function importarProveedores(
  filas: FilaImportacionProveedor[],
  soloValidar = false
): Promise<ResultadoImportacionProveedores> {
  const { data } = await axiosClient.post<ResultadoImportacionProveedores>(
    "/api/admin/proveedores/importar",
    { filas, soloValidar }
  );
  return data;
}
